import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, FormControlLabel, FormHelperText, InputLabel, Stack, Switch, Typography } from '@mui/material';
import {
  SaveContextProvider,
  useRefresh,
  useNotify,
  Toolbar,
  SaveButton,
  useDataProvider,
  useResourceContext,
  SimpleForm,
  SelectArrayInput,
  BooleanField,
  BooleanInput,
  CloneButton,
  Datagrid,
  DateField,
  EditButton,
  NumberField,
  NumberInput,
  ReferenceManyField,
  SelectInput,
  ShowContextProvider,
  ShowView,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  useShowController
} from 'react-admin';
import ContextTitle from '../../Components/ContextTitle';
import { LoginAsButton } from '../../Components/LoginAsButton';
import DeactivatedDateField from '../../Components/StyledFields/DeactivatedDateField';
import { superAdminApiServer } from '../../lib/config';
import { getBearerToken } from '../../lib/Auth';
import businessRulesMappings from './businessRulesMappings';
import { sortBusinessRules } from './businessRulesOrder';
import { startCase } from 'lodash';

const AddDashboardToolbar = () => {
  return (
    <Toolbar>
      <SaveButton label="Add Dashboards" type="button" />
    </Toolbar>
  );
};

const getBusinessRuleLabel = (businessRuleTypeName: string): string => businessRulesMappings[businessRuleTypeName] ?? startCase(businessRuleTypeName);

interface BusinessRuleValueInputProps {
  source: string;
  label: string;
  dataType?: string;
  enumValues?: string[];
}

interface BusinessRuleSaveResult {
  businessRuleTypeName: string;
  status: string;
}

const BusinessRuleValueInput = ({ source, label, dataType, enumValues }: BusinessRuleValueInputProps) => {
  if (dataType === 'boolean') {
    return <BooleanInput source={source} label={label} format={(value) => value === true || value === 'true'} parse={(value) => (value ? 'true' : 'false')} />;
  }

  let control;
  switch (dataType) {
    case 'integer':
      control = <NumberInput source={source} label={false} parse={(value) => (value == null ? '' : String(value))} />;
      break;
    case 'enum':
      control = <SelectInput source={source} label={false} choices={(enumValues ?? []).map((option) => ({ id: option, name: option }))} />;
      break;
    case 'time':
      control = <TextInput source={source} label={false} type="time" />;
      break;
    default:
      control = <TextInput source={source} label={false} />;
  }

  return (
    <Stack spacing={0.5}>
      <InputLabel>{label}</InputLabel>
      {control}
    </Stack>
  );
};

const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const BusinessRulesToolbar = () => {
  return (
    <Toolbar>
      <SaveButton label="Save changes" type="button" alwaysEnable onClick={blurActiveElement} />
    </Toolbar>
  );
};

const CompanyShow = () => {
  const controllerProps = useShowController();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const location = useLocation();
  const resource = useResourceContext();
  const currentTabId = Number(location.pathname.split('show/')[1]) || 0;
  const dashboardsTabIndex = resource === 'companies' ? 4 : 3;
  const businessRulesTabIndex = dashboardsTabIndex + 1;

  const [globalDashboards, setGlobalDashboards] = useState<any[]>([]);
  const [webLogoUrl, setWebLogoUrl] = useState<string | null>(null);
  const [kioskLogoUrl, setKioskLogoUrl] = useState<string | null>(null);
  const [kioskPrimaryColor, setKioskPrimaryColor] = useState<string>('#000000');
  const [hasStoredColor, setHasStoredColor] = useState(false);
  const [whitelabellingLoading, setWhitelabellingLoading] = useState(false);
  const [whitelabellingFetching, setWhitelabellingFetching] = useState(false);
  const [whitelabellingLoaded, setWhitelabellingLoaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const webFileInputRef = useRef<HTMLInputElement>(null);

  const [businessRules, setBusinessRules] = useState<any[]>([]);
  const [businessRulesFormRecord, setBusinessRulesFormRecord] = useState<{ values: Record<string, string> }>({ values: {} });
  const [businessRulesLoadedFor, setBusinessRulesLoadedFor] = useState<string | undefined>(undefined);
  const businessRuleValuesRef = useRef<Record<string, string>>({});
  const companyUuid = controllerProps.record?.uuid as string | undefined;

  const [monthlyAccrualEnabled, setMonthlyAccrualEnabled] = useState(false);
  // undefined = never configured for this company: oitchau-admin falls back to the `vacation-policy` LaunchDarkly flag.
  const [vacationPolicyEnabled, setVacationPolicyEnabled] = useState<boolean | undefined>(undefined);
  const [settingsLoading, setSettingsLoading] = useState(false);
  // Baselines (last loaded/saved values), so the common Save button knows which overrides changed.
  const monthlyAccrualLoadedRef = useRef(false);
  const vacationPolicyLoadedRef = useRef<boolean | undefined>(undefined);

  const fetchBusinessRules = useCallback(
    (signal?: AbortSignal) =>
      fetch(`${superAdminApiServer}/companies/${companyUuid}/business-rules`, {
        headers: { Authorization: getBearerToken() },
        signal
      })
        .then((r) => {
          if (!r.ok) throw new Error(`Failed to load business rules: ${r.status}`);
          return r.json();
        })
        .then((data) => {
          const sortedRules = sortBusinessRules(data);
          setBusinessRules(sortedRules);
          const values = Object.fromEntries(sortedRules.map((rule: any) => [rule.businessRuleTypeName, rule.value ?? '']));
          businessRuleValuesRef.current = values;
          setBusinessRulesFormRecord({ values });
        }),
    [companyUuid]
  );

  useEffect(() => {
    if (currentTabId !== businessRulesTabIndex || !companyUuid || resource !== 'companies' || businessRulesLoadedFor === companyUuid) return;
    const controller = new AbortController();
    fetchBusinessRules(controller.signal)
      .then(() => setBusinessRulesLoadedFor(companyUuid))
      .catch((err) => {
        if ((err as { name?: string }).name !== 'AbortError') {
          notify('Failed to load business rules', { type: 'error' });
        }
      });
    return () => controller.abort();
  }, [currentTabId, businessRulesTabIndex, companyUuid, resource, businessRulesLoadedFor, fetchBusinessRules, notify]);

  // Load the resolved settings (company ?? partner ?? country) when the Business Rules tab activates.
  useEffect(() => {
    if (currentTabId !== businessRulesTabIndex || !companyUuid || resource !== 'companies') return;
    const controller = new AbortController();
    setSettingsLoading(true);
    fetch(`${superAdminApiServer}/companies/${companyUuid}/config/settings`, {
      headers: { Authorization: getBearerToken() },
      signal: controller.signal
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load settings: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const resolved = !!data?.content?.monthlyAccrualEnabled;
        monthlyAccrualLoadedRef.current = resolved;
        setMonthlyAccrualEnabled(resolved);
        const vacationPolicy = typeof data?.content?.vacationPolicyEnabled === 'boolean' ? data.content.vacationPolicyEnabled : undefined;
        vacationPolicyLoadedRef.current = vacationPolicy;
        setVacationPolicyEnabled(vacationPolicy);
      })
      .catch((err) => {
        if ((err as { name?: string }).name !== 'AbortError') {
          notify('Failed to load company override settings', { type: 'error' });
        }
      })
      .finally(() => setSettingsLoading(false));
    return () => controller.abort();
  }, [currentTabId, businessRulesTabIndex, companyUuid, resource, notify]);

  useEffect(() => {
    if (currentTabId !== 3 || !companyUuid || resource !== 'companies') return;
    const controller = new AbortController();
    setWhitelabellingFetching(true);
    setWhitelabellingLoaded(false);
    setWebLogoUrl(null);
    setKioskLogoUrl(null);
    setKioskPrimaryColor('#000000');
    setHasStoredColor(false);
    fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling`, {
      headers: { Authorization: getBearerToken() },
      signal: controller.signal
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load whitelabelling settings: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setWebLogoUrl(data.webLogoUrl ?? null);
        setKioskLogoUrl(data.kioskLogoUrl ?? null);
        setKioskPrimaryColor(data.kioskPrimaryColor ?? '#000000');
        setHasStoredColor(!!data.kioskPrimaryColor);
        setWhitelabellingLoaded(true);
      })
      .catch((err) => {
        if ((err as { name?: string }).name !== 'AbortError') {
          notify('Failed to load whitelabelling settings', { type: 'error' });
        }
      })
      .finally(() => setWhitelabellingFetching(false));
    return () => controller.abort();
  }, [currentTabId, companyUuid, notify, resource]);

  const handleLogoUpload = async (file: File) => {
    if (!companyUuid) return;
    setWhitelabellingLoading(true);
    const formData = new FormData();
    formData.append('kiosk_logo', file);
    try {
      const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling/logo`, {
        method: 'POST',
        headers: { Authorization: getBearerToken() },
        body: formData
      });
      if (!r.ok) throw new Error(`Upload failed with status ${r.status}`);
      const data = await r.json();
      setKioskLogoUrl(data.kioskLogoUrl);
      if (fileInputRef.current) fileInputRef.current.value = '';
      notify('Kiosk logo uploaded', { type: 'success' });
    } catch {
      notify('Kiosk logo upload failed', { type: 'error' });
    } finally {
      setWhitelabellingLoading(false);
    }
  };

  const handleWebLogoUpload = async (file: File) => {
    if (!companyUuid) return;
    setWhitelabellingLoading(true);
    const formData = new FormData();
    formData.append('web_logo', file);
    try {
      const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling/web-logo`, {
        method: 'POST',
        headers: { Authorization: getBearerToken() },
        body: formData
      });
      if (!r.ok) throw new Error(`Upload failed with status ${r.status}`);
      const data = await r.json();
      setWebLogoUrl(data.webLogoUrl);
      if (webFileInputRef.current) webFileInputRef.current.value = '';
      notify('Web logo uploaded', { type: 'success' });
    } catch {
      notify('Web logo upload failed', { type: 'error' });
    } finally {
      setWhitelabellingLoading(false);
    }
  };

  const handleSaveColor = async () => {
    if (!companyUuid) return;
    setWhitelabellingLoading(true);
    try {
      const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling`, {
        method: 'PUT',
        headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kioskPrimaryColor })
      });
      if (!r.ok) throw new Error(`Save failed with status ${r.status}`);
      setHasStoredColor(true);
      notify('Color saved', { type: 'success' });
    } catch {
      notify('Failed to save color', { type: 'error' });
    } finally {
      setWhitelabellingLoading(false);
    }
  };

  const applyBusinessRulesSaveResults = (results: BusinessRuleSaveResult[], changedRulesCount: number, formData: { values: Record<string, string> }) => {
    const updateRules = results.filter((result) => result.status === 'updated').map((result) => result.businessRuleTypeName);
    if (!updateRules.length) throw new Error('None updated');

    const updatedNames = new Set(updateRules);
    updatedNames.forEach((name) => {
      businessRuleValuesRef.current[name] = formData.values[name];
    });
    if (updateRules.length === changedRulesCount) {
      notify('Business rules saved', { type: 'success' });
    } else {
      const failedLabels = results
        .filter((result) => !updatedNames.has(result.businessRuleTypeName))
        .map((result) => getBusinessRuleLabel(result.businessRuleTypeName))
        .join(', ');
      notify(`${updateRules.length} rule(s) saved. Failed to update: ${failedLabels}`, { type: 'warning' });
    }
  };

  const handleSaveBusinessRules = async (formData: { values: Record<string, string> }) => {
    if (!companyUuid) return;
    const changedRules = businessRules.filter((rule) => String(formData.values[rule.businessRuleTypeName] ?? '') !== String(businessRuleValuesRef.current[rule.businessRuleTypeName] ?? ''));

    if (changedRules.length) {
      try {
        const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/business-rules`, {
          method: 'PATCH',
          headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rules: changedRules.map((rule) => ({ businessRuleTypeName: rule.businessRuleTypeName, value: formData.values[rule.businessRuleTypeName] }))
          })
        });

        if (!r.ok) throw new Error(`Save failed with status ${r.status}`);
        const body = await r.json().catch(() => null);
        if (!Array.isArray(body?.results)) throw new Error('Unexpected response format');
        applyBusinessRulesSaveResults(body.results, changedRules.length, formData);
      } catch {
        notify('Failed to save business rules', { type: 'error' });
      }
    }

    // Company override (extra_options.settings) — saved together with the business rules via the common button.
    // Only changed keys are sent, so an untouched unset vacation-policy override stays unset.
    const settingsPatch: { monthlyAccrualEnabled?: boolean; vacationPolicyEnabled?: boolean } = {};
    if (monthlyAccrualEnabled !== monthlyAccrualLoadedRef.current) settingsPatch.monthlyAccrualEnabled = monthlyAccrualEnabled;
    if (vacationPolicyEnabled !== vacationPolicyLoadedRef.current) settingsPatch.vacationPolicyEnabled = vacationPolicyEnabled;
    if (Object.keys(settingsPatch).length) {
      try {
        const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/config/settings`, {
          method: 'PATCH',
          headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
          body: JSON.stringify(settingsPatch)
        });
        if (!r.ok) throw new Error(`Save failed with status ${r.status}`);
        monthlyAccrualLoadedRef.current = monthlyAccrualEnabled;
        vacationPolicyLoadedRef.current = vacationPolicyEnabled;
        notify('Company overrides saved', { type: 'success' });
      } catch {
        setMonthlyAccrualEnabled(monthlyAccrualLoadedRef.current);
        setVacationPolicyEnabled(vacationPolicyLoadedRef.current);
        notify('Failed to save company overrides', { type: 'error' });
      }
    }
  };

  if (currentTabId === dashboardsTabIndex && !globalDashboards.length) {
    dataProvider.getList('dashboards', { filter: {}, pagination: { page: 1, perPage: 500 }, sort: { field: 'created_at', order: 'DESC' } }).then((response) => {
      const dashboards = response.data.map((dashboard: any) => {
        return { id: dashboard.id, name: dashboard.name };
      }) as any[];

      setGlobalDashboards(dashboards);
    });
  }

  const handleAddDashboards = (formData: { dashboards: string[] }) => {
    formData.dashboards.forEach((dashboardId: string) => {
      dataProvider
        .create('company-dashboards', {
          data: {
            dashboardId: Number(dashboardId),
            companyId: controllerProps.record.id
          }
        })
        .then(() => {
          refresh();
          notify('Dashboard successfully added', { type: 'success' });
        });
    });
  };

  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView title={<ContextTitle context="name" />}>
        <TabbedShowLayout>
          <Tab label="Summary">
            <TextField source="id" />
            <TextField source="uuid" />
            <TextField source="name" />
            <TextField source="countryCode" label="Country" emptyText="—" />
            <BooleanField source="active" />
            <DeactivatedDateField source="deactivatedAt" />
            <TextField source="deactivatedBy" />
            <TextField source="deactivationReason" />
            <TextField source="sourcePlatform" />
            <TextField source="sourceType" />
            <DateField source="createdAt" />
            <DateField source="updatedAt" />
            <DateField source="settingsUpdatedAt" />
          </Tab>
          <Tab label="Miscellaneous">
            <TextField source="locale" />
            <TextField source="currency" />
            <TextField source="hubspotId" />
            <TextField source="gainsightId" />
            <TextField source="taxPayerId" />
            <TextField source="slug" />
            <TextField source="companyType" />
            <NumberField source="logoId" />
            <NumberField source="documentLogoId" />
            <NumberField source="creator" />
            <NumberField source="defaultLocation" />
            <TextField source="tabletCode" />
            <TextField source="referredById" />
            <TextField source="beaconManufacture" />
            <CloneButton />
          </Tab>
          <Tab label="Owners">
            <ReferenceManyField label="Owners" reference="profiles" target="company" sort={{ field: 'created_at', order: 'DESC' }} filter={{ role: 'employer' }}>
              <Datagrid>
                <TextField source="fullName" />
                <TextField source="role" />
                <DateField source="created_at" />
                <LoginAsButton fullNameSource="user.fullName" />
                <EditButton />
              </Datagrid>
            </ReferenceManyField>
          </Tab>
          {resource === 'companies' && (
            <Tab label="Whitelabelling">
              <div style={{ padding: '16px' }}>
                <Box sx={{ mb: 3, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Web logo</div>
                  <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>The background will be the same as the brand color, so please avoid using dark colors for the logo. Only PNG accepted.</Box>
                  {webLogoUrl && (
                    <div style={{ width: 280, height: 90, background: kioskPrimaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, marginBottom: 8, overflow: 'hidden' }}>
                      <img src={webLogoUrl} alt="Web logo" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
                    </div>
                  )}
                  <input
                    ref={webFileInputRef}
                    type="file"
                    accept="image/png"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleWebLogoUpload(file);
                    }}
                  />
                  <button disabled={whitelabellingLoading || whitelabellingFetching} onClick={() => webFileInputRef.current?.click()} style={{ marginRight: 8 }}>
                    {webLogoUrl ? 'Replace logo' : 'Upload logo'}
                  </button>
                  {webLogoUrl && (
                    <button
                      disabled={whitelabellingLoading || whitelabellingFetching}
                      onClick={async () => {
                        if (!companyUuid) return;
                        try {
                          const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling`, {
                            method: 'PUT',
                            headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ webLogoUrl: null })
                          });
                          if (!r.ok) throw new Error(`Remove failed with status ${r.status}`);
                          setWebLogoUrl(null);
                          notify('Web logo removed', { type: 'success' });
                        } catch {
                          notify('Failed to remove web logo', { type: 'error' });
                        }
                      }}
                    >
                      Remove logo
                    </button>
                  )}
                </Box>

                <Box sx={{ mb: 3, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Kiosk logo</div>
                  <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>This is the logo that will be displayed on the main page of the kiosk. Please keep in mind that the background there is white, so avoid using a white logo. Only PNG accepted.</Box>
                  {kioskLogoUrl && <Box component="img" src={kioskLogoUrl} alt="Kiosk logo" sx={{ maxWidth: 300, maxHeight: 140, display: 'block', mb: 1, border: 1, borderColor: 'divider', bgcolor: '#fff' }} />}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                    }}
                  />
                  <button disabled={whitelabellingLoading || whitelabellingFetching} onClick={() => fileInputRef.current?.click()} style={{ marginRight: 8 }}>
                    {kioskLogoUrl ? 'Replace logo' : 'Upload logo'}
                  </button>
                  {kioskLogoUrl && (
                    <button
                      disabled={whitelabellingLoading || whitelabellingFetching}
                      onClick={async () => {
                        if (!companyUuid) return;
                        try {
                          const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling`, {
                            method: 'PUT',
                            headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
                            body: JSON.stringify({ kioskLogoUrl: null })
                          });
                          if (!r.ok) throw new Error(`Remove failed with status ${r.status}`);
                          setKioskLogoUrl(null);
                          notify('Logo removed', { type: 'success' });
                        } catch {
                          notify('Failed to remove logo', { type: 'error' });
                        }
                      }}
                    >
                      Remove logo
                    </button>
                  )}
                </Box>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Brand Color</div>
                  <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>This is the color of top bar in web.</Box>
                  <input type="color" disabled={whitelabellingFetching} value={kioskPrimaryColor} onChange={(e) => setKioskPrimaryColor(e.target.value)} style={{ width: 48, height: 36, cursor: whitelabellingFetching ? 'default' : 'pointer', marginRight: 12 }} />
                  <span>{kioskPrimaryColor}</span>
                </div>
                <button disabled={whitelabellingLoading || whitelabellingFetching || !whitelabellingLoaded} onClick={handleSaveColor} style={{ padding: '8px 16px', marginRight: 8 }}>
                  {whitelabellingLoading ? 'Saving…' : 'Save color'}
                </button>
                {hasStoredColor && (
                  <button
                    disabled={whitelabellingLoading || whitelabellingFetching}
                    onClick={async () => {
                      if (!companyUuid) return;
                      setWhitelabellingLoading(true);
                      try {
                        const r = await fetch(`${superAdminApiServer}/companies/${companyUuid}/whitelabelling`, {
                          method: 'PUT',
                          headers: { Authorization: getBearerToken(), 'Content-Type': 'application/json' },
                          body: JSON.stringify({ kioskPrimaryColor: null })
                        });
                        if (!r.ok) throw new Error(`Reset failed with status ${r.status}`);
                        setKioskPrimaryColor('#000000');
                        setHasStoredColor(false);
                        notify('Color reset to default', { type: 'success' });
                      } catch {
                        notify('Failed to reset color', { type: 'error' });
                      } finally {
                        setWhitelabellingLoading(false);
                      }
                    }}
                    style={{ padding: '8px 16px' }}
                  >
                    Reset color
                  </button>
                )}
              </div>
            </Tab>
          )}
          <Tab label="Dashboards">
            <ReferenceManyField label="Dashboards" reference="company-dashboards" target="company" sort={{ field: 'created_at', order: 'DESC' }}>
              <Datagrid>
                <TextField source="dashboard.name" />
                <DateField source="createdAt" />
                <DateField source="updatedAt" />
              </Datagrid>
            </ReferenceManyField>
            <SaveContextProvider value={{ save: handleAddDashboards }}>
              <SimpleForm toolbar={<AddDashboardToolbar />}>
                <SelectArrayInput label="Add Dashboards" source="dashboards" choices={globalDashboards} />
              </SimpleForm>
            </SaveContextProvider>
          </Tab>
          {resource === 'companies' && (
            <Tab label="Business Rules">
              <Box sx={{ p: 2, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Company overrides
                </Typography>
                <FormControlLabel control={<Switch checked={monthlyAccrualEnabled} disabled={settingsLoading} onChange={(e) => setMonthlyAccrualEnabled(e.target.checked)} />} label="Monthly accrual enabled" />
                <FormHelperText>Sets a company-level override; the value shown is the resolved setting (company override, otherwise the country default). Saved with “Save changes”.</FormHelperText>
                <FormControlLabel control={<Switch checked={vacationPolicyEnabled === true} disabled={settingsLoading} onChange={(e) => setVacationPolicyEnabled(e.target.checked)} />} label="Vacation policies" />
                <FormHelperText>
                  {vacationPolicyEnabled === undefined ? 'Not set — follows the vacation-policy LaunchDarkly flag. Toggling sets a company-level override.' : 'Company-level override; takes precedence over the vacation-policy LaunchDarkly flag.'} Saved with “Save changes”.
                </FormHelperText>
              </Box>
              <SaveContextProvider value={{ save: handleSaveBusinessRules }}>
                <SimpleForm record={businessRulesFormRecord} toolbar={<BusinessRulesToolbar />}>
                  {businessRules.map((rule) => (
                    <BusinessRuleValueInput key={rule.businessRuleTypeName} source={`values.${rule.businessRuleTypeName}`} label={getBusinessRuleLabel(rule.businessRuleTypeName)} dataType={rule.dataType} enumValues={rule.enumValues} />
                  ))}
                </SimpleForm>
              </SaveContextProvider>
            </Tab>
          )}
        </TabbedShowLayout>
      </ShowView>
    </ShowContextProvider>
  );
};

export default CompanyShow;
